import React, { useRef, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { EffectComposer, Bloom, Vignette } from "@react-three/postprocessing";
import * as THREE from "three";
import api from "@/lib/api";

const GALAXY_R = 185;

/* ---------- procedural soft sprite textures ---------- */
function radialTexture(size, stops) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  stops.forEach(([o, col]) => g.addColorStop(o, col));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// soft, irregular cloud texture (fractal blobs) for volumetric gas
function cloudTexture(size) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  let seed = 12345;
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  for (let i = 0; i < 26; i++) {
    const cx = size * (0.25 + rnd() * 0.5);
    const cy = size * (0.25 + rnd() * 0.5);
    const rad = size * (0.12 + rnd() * 0.32);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
    const a = 0.05 + rnd() * 0.09;
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(0.5, `rgba(255,255,255,${a * 0.4})`);
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function useSprites() {
  return useMemo(() => ({
    star: radialTexture(64, [[0, "rgba(255,255,255,1)"], [0.22, "rgba(255,255,255,0.9)"], [0.5, "rgba(255,255,255,0.22)"], [1, "rgba(255,255,255,0)"]]),
    glow: radialTexture(128, [[0, "rgba(255,255,255,0.7)"], [0.35, "rgba(255,255,255,0.25)"], [1, "rgba(255,255,255,0)"]]),
    cloud: cloudTexture(256),
    core: radialTexture(256, [[0, "rgba(255,246,220,1)"], [0.18, "rgba(255,228,168,0.92)"], [0.45, "rgba(255,188,104,0.4)"], [1, "rgba(255,150,60,0)"]]),
  }), []);
}

const seeded = (s0) => { let s = s0 + 1; return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; }; };
const gauss = (rnd) => (rnd() + rnd() + rnd() - 1.5) * 0.85;

/* ---------- dense central bulge (volumetric 3D core) ---------- */
function Bulge({ sprite }) {
  const geo = useMemo(() => {
    const count = 42000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const inner = new THREE.Color("#fff3cf");
    const outer = new THREE.Color("#ffb765");
    const c = new THREE.Color();
    const rnd = seeded(101);
    for (let i = 0; i < count; i++) {
      const rr = Math.pow(rnd(), 2.4) * 46;
      const u = rnd() * 2 - 1;
      const phi = rnd() * Math.PI * 2;
      const sq = Math.sqrt(1 - u * u);
      positions[i * 3] = rr * sq * Math.cos(phi);
      positions[i * 3 + 1] = rr * u * 0.55;
      positions[i * 3 + 2] = rr * sq * Math.sin(phi);
      c.lerpColors(inner, outer, Math.min(1, rr / 46));
      const b = 0.75 + rnd() * 0.45;
      colors[i * 3] = c.r * b; colors[i * 3 + 1] = c.g * b; colors[i * 3 + 2] = c.b * b;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return g;
  }, []);
  return (
    <points geometry={geo}>
      <pointsMaterial size={1.3} map={sprite} vertexColors transparent alphaTest={0.01}
        opacity={0.9} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

/* ---------- log-spiral disk star layer ---------- */
function DiskLayer({ count, size, sprite, brightness, scatter, seedOffset }) {
  const geo = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const ARMS = 2;
    const TWIST = 2.6;
    const core = new THREE.Color("#ffe9c2");
    const mid = new THREE.Color("#ffffff");
    const edge = new THREE.Color("#a9c6ff");
    const c = new THREE.Color();
    const rnd = seeded(seedOffset);
    for (let i = 0; i < count; i++) {
      const t = Math.pow(rnd(), 0.55);
      const r = 12 + t * (GALAXY_R - 12);
      const arm = i % ARMS;
      const armAngle = (arm / ARMS) * Math.PI * 2;
      const spiral = TWIST * Math.log(r / 8);
      const armSpread = (0.18 + (1 - r / GALAXY_R) * 0.55) * scatter;
      const theta = armAngle + spiral + gauss(rnd) * armSpread;
      const rj = r + gauss(rnd) * 4;
      const thickness = 2.2 + (1 - r / GALAXY_R) * 7;
      positions[i * 3] = Math.cos(theta) * rj;
      positions[i * 3 + 1] = gauss(rnd) * thickness;
      positions[i * 3 + 2] = Math.sin(theta) * rj;
      const tt = r / GALAXY_R;
      if (tt < 0.3) c.lerpColors(core, mid, tt / 0.3);
      else c.lerpColors(mid, edge, (tt - 0.3) / 0.7);
      const b = brightness * (0.55 + rnd() * 0.45);
      colors[i * 3] = c.r * b; colors[i * 3 + 1] = c.g * b; colors[i * 3 + 2] = c.b * b;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return g;
  }, [count, size, brightness, scatter, seedOffset]);
  return (
    <points geometry={geo}>
      <pointsMaterial size={size} map={sprite} vertexColors transparent alphaTest={0.01}
        opacity={0.95} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

/* ---------- faint spherical halo ---------- */
function Halo({ sprite }) {
  const geo = useMemo(() => {
    const count = 9000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const col = new THREE.Color("#9fb6ff");
    const rnd = seeded(555);
    for (let i = 0; i < count; i++) {
      const rr = 40 + Math.pow(rnd(), 0.5) * GALAXY_R * 1.35;
      const u = rnd() * 2 - 1;
      const phi = rnd() * Math.PI * 2;
      const sq = Math.sqrt(1 - u * u);
      positions[i * 3] = rr * sq * Math.cos(phi);
      positions[i * 3 + 1] = rr * u * 0.7;
      positions[i * 3 + 2] = rr * sq * Math.sin(phi);
      const b = 0.25 + rnd() * 0.35;
      colors[i * 3] = col.r * b; colors[i * 3 + 1] = col.g * b; colors[i * 3 + 2] = col.b * b;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return g;
  }, []);
  return (
    <points geometry={geo}>
      <pointsMaterial size={1.0} map={sprite} vertexColors transparent alphaTest={0.01}
        opacity={0.6} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

/* ---------- volumetric gas & dust clouds (camera flies through) ---------- */
function GasClouds({ sprite }) {
  const clouds = useMemo(() => {
    // emissive nebula palette (H-II reds/pinks, reflection blues, dusty golds/teals)
    const palette = ["#ff5c7a", "#ff8a4b", "#ffcf7a", "#5b8bff", "#8f6bff", "#3bd6c0", "#c85bff", "#ff9ecf"];
    const arr = [];
    const ARMS = 2;
    const TWIST = 2.6;
    const rnd = seeded(77);
    // arm-following gas
    for (let i = 0; i < 320; i++) {
      const t = Math.pow(rnd(), 0.6);
      const r = 16 + t * (GALAXY_R - 16);
      const arm = i % ARMS;
      const spiral = TWIST * Math.log(r / 8);
      const theta = (arm / ARMS) * Math.PI * 2 + spiral + gauss(rnd) * 0.4;
      const x = Math.cos(theta) * r + gauss(rnd) * 9;
      const z = Math.sin(theta) * r + gauss(rnd) * 9;
      const y = gauss(rnd) * (4 + (1 - r / GALAXY_R) * 8);
      const scale = 22 + rnd() * 70;
      const color = palette[Math.floor(rnd() * palette.length)];
      const opacity = 0.04 + rnd() * 0.10;
      const rot = rnd() * Math.PI * 2;
      arr.push({ pos: [x, y, z], scale, color, opacity, rot, key: i });
    }
    // a few huge, faint background sheets for depth
    for (let i = 0; i < 26; i++) {
      const r = Math.pow(rnd(), 0.5) * GALAXY_R * 1.1;
      const theta = rnd() * Math.PI * 2;
      const x = Math.cos(theta) * r;
      const z = Math.sin(theta) * r;
      const y = gauss(rnd) * 26;
      const scale = 120 + rnd() * 180;
      const color = palette[Math.floor(rnd() * palette.length)];
      arr.push({ pos: [x, y, z], scale, color, opacity: 0.02 + rnd() * 0.03, rot: rnd() * Math.PI * 2, key: 1000 + i });
    }
    return arr;
  }, []);
  return (
    <group>
      {clouds.map((c) => (
        <sprite key={c.key} position={c.pos} scale={[c.scale, c.scale, 1]} material-rotation={c.rot}>
          <spriteMaterial map={sprite} color={c.color} transparent opacity={c.opacity}
            depthWrite={false} blending={THREE.AdditiveBlending} />
        </sprite>
      ))}
    </group>
  );
}

/* ---------- luminous core ---------- */
function Core({ sprites }) {
  return (
    <group>
      <sprite scale={[260, 260, 1]}><spriteMaterial map={sprites.core} transparent opacity={0.45} depthWrite={false} blending={THREE.AdditiveBlending} /></sprite>
      <sprite scale={[120, 120, 1]}><spriteMaterial map={sprites.core} transparent opacity={0.7} depthWrite={false} blending={THREE.AdditiveBlending} /></sprite>
      <sprite scale={[48, 48, 1]}><spriteMaterial map={sprites.glow} color="#fff4d6" transparent opacity={0.95} depthWrite={false} blending={THREE.AdditiveBlending} /></sprite>
      <sprite scale={[16, 16, 1]}><spriteMaterial map={sprites.star} color="#fffaf0" transparent opacity={1} depthWrite={false} blending={THREE.AdditiveBlending} /></sprite>
      <mesh><sphereGeometry args={[3, 24, 24]} /><meshBasicMaterial color="#fff8e6" /></mesh>
    </group>
  );
}

/* ---------- rotating galaxy assembly ---------- */
function GalaxyBody({ sprites }) {
  const ref = useRef();
  useFrame((_, d) => { if (ref.current) ref.current.rotation.y += d * 0.005; });
  return (
    <group ref={ref}>
      <GasClouds sprite={sprites.cloud} />
      <Halo sprite={sprites.star} />
      <DiskLayer count={160000} size={0.9} sprite={sprites.star} brightness={0.85} scatter={1.0} seedOffset={11} />
      <DiskLayer count={55000} size={1.6} sprite={sprites.star} brightness={1.05} scatter={0.85} seedOffset={23} />
      <DiskLayer count={9000} size={2.9} sprite={sprites.glow} brightness={1.3} scatter={1.1} seedOffset={37} />
      <Bulge sprite={sprites.star} />
      <Core sprites={sprites} />
    </group>
  );
}

/* ---------- clickable markers ---------- */
function Marker({ obj, hero, selected, onSelect, onDeepDive, hovered, setHovered, sprite }) {
  const [x, z] = obj.galaxy_pos;
  const active = hovered === obj.id || selected;
  const size = hero ? (obj.type === "galaxy" ? 4.5 : obj.type === "black_hole" ? 3.5 : 2.2) : 0.9;
  const showLabel = hero || active;
  const glowOpacity = hero ? 0.9 : active ? 0.85 : 0.28;
  const glowScale = hero ? size * 6 : active ? size * 5 : size * 2.4;
  return (
    <group position={[x, 0, z]}>
      <sprite scale={[glowScale, glowScale, 1]}>
        <spriteMaterial map={sprite} color={obj.color} transparent opacity={glowOpacity} depthWrite={false} blending={THREE.AdditiveBlending} />
      </sprite>
      <mesh
        onClick={(e) => { e.stopPropagation(); onSelect(obj.id); }}
        onDoubleClick={(e) => { e.stopPropagation(); onDeepDive(obj.id); }}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(obj.id); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHovered(null); document.body.style.cursor = "auto"; }}
      >
        <sphereGeometry args={[Math.max(size, 1.1), 18, 18]} />
        <meshBasicMaterial color={obj.color} transparent opacity={hero ? 1 : 0.9} />
      </mesh>
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[size * 2.3, size * 2.7, 48]} />
          <meshBasicMaterial color="#00D8FF" side={THREE.DoubleSide} />
        </mesh>
      )}
      {showLabel && (
        <Html position={[0, size + 3, 0]} center distanceFactor={hero ? 130 : 95} zIndexRange={[10, 0]}>
          <span className="text-tel text-[9px] whitespace-nowrap px-1"
            style={{ color: hero ? obj.color : "rgba(255,255,255,0.9)", textShadow: "0 0 6px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1)" }}>
            {obj.name}
          </span>
        </Html>
      )}
    </group>
  );
}

function SunMarker({ pos, onReturn, sprite }) {
  const [x, z] = pos;
  const ref = useRef();
  useFrame((_, d) => { if (ref.current) ref.current.rotation.z += d; });
  return (
    <group position={[x, 0, z]}>
      <sprite scale={[16, 16, 1]}><spriteMaterial map={sprite} color="#00D8FF" transparent opacity={0.9} depthWrite={false} blending={THREE.AdditiveBlending} /></sprite>
      <mesh onClick={(e) => { e.stopPropagation(); onReturn(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "auto"; }}>
        <sphereGeometry args={[1.6, 16, 16]} /><meshBasicMaterial color="#00D8FF" />
      </mesh>
      <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[3, 3.6, 48]} /><meshBasicMaterial color="#00D8FF" side={THREE.DoubleSide} transparent opacity={0.85} />
      </mesh>
      <Html position={[0, 6, 0]} center distanceFactor={120}>
        <span className="text-tel text-[9px] whitespace-nowrap text-cyan-300" style={{ textShadow: "0 0 8px rgba(0,0,0,0.9)" }}>◄ Solar System</span>
      </Html>
    </group>
  );
}

/* ---------- constellations ---------- */
function Constellations() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/galaxy/constellations").then((r) => setData(r.data.constellations)); }, []);
  return (
    <group>
      {data.map((c, ci) => {
        const F = 1.3 * (c.scale || 1);
        const pts = c.nodes.map((n) => new THREE.Vector3(c.anchor[0] + n[0] * F, 3, c.anchor[1] + n[1] * F));
        return (
          <group key={ci}>
            {c.edges.map((e, ei) => {
              const g = new THREE.BufferGeometry().setFromPoints([pts[e[0]], pts[e[1]]]);
              return (
                <line key={ei} geometry={g}>
                  <lineBasicMaterial color="#8fd4ff" transparent opacity={0.55} />
                </line>
              );
            })}
            {pts.map((p, pi) => (
              <mesh key={pi} position={p}><sphereGeometry args={[0.7, 10, 10]} /><meshBasicMaterial color="#cfeaff" /></mesh>
            ))}
            <Html position={[c.anchor[0], 3, c.anchor[1] - 12 * F]} center distanceFactor={150}>
              <span className="text-tel text-[9px] whitespace-nowrap" style={{ color: "rgba(143,212,255,0.9)", textShadow: "0 0 8px rgba(0,0,0,0.9)" }}>{c.name}</span>
            </Html>
          </group>
        );
      })}
    </group>
  );
}

/* ---------- camera focus ---------- */
function GalaxyCamera({ focusId, focusToken, posMap, controlsRef }) {
  const { camera } = useThree();
  const anim = useRef({ active: false, t: 0, from: new THREE.Vector3(), fromT: new THREE.Vector3() });
  useEffect(() => {
    if (focusToken === 0) return;
    const a = anim.current;
    a.active = true; a.t = 0;
    a.from.copy(camera.position);
    a.fromT.copy(controlsRef.current ? controlsRef.current.target : new THREE.Vector3());
  }, [focusToken]); // eslint-disable-line
  useFrame((_, delta) => {
    const a = anim.current;
    if (!a.active) return;
    a.t = Math.min(1, a.t + delta / 1.6);
    const e = a.t < 0.5 ? 4 * a.t ** 3 : 1 - Math.pow(-2 * a.t + 2, 3) / 2;
    const p = posMap[focusId] || [0, 0];
    const target = new THREE.Vector3(p[0], 0, p[1]);
    const desired = new THREE.Vector3(p[0] + 20, 24, p[1] + 32);
    camera.position.lerpVectors(a.from, desired, e);
    if (controlsRef.current) { controlsRef.current.target.lerpVectors(a.fromT, target, e); controlsRef.current.update(); }
    if (a.t >= 1) a.active = false;
  });
  return null;
}

function FocusTrail({ focusId, focusToken, posMap }) {
  const geom = useMemo(() => new THREE.BufferGeometry(), []);
  const lineMat = useMemo(() => new THREE.LineBasicMaterial({ color: "#00E5FF", transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }), []);
  const dotRef = useRef();
  const st = useRef({ active: false, t: 0, start: new THREE.Vector3(), end: new THREE.Vector3(), has: false });

  useEffect(() => {
    if (focusToken === 0) return;
    const p = posMap[focusId];
    if (!p) return;
    const s = st.current;
    s.start.copy(s.has ? s.end : new THREE.Vector3(0, 0, 0));
    s.end.set(p[0], 0, p[1]);
    s.active = true; s.t = 0; s.has = true;
  }, [focusToken]); // eslint-disable-line

  useFrame((_, d) => {
    const s = st.current;
    if (!s.active) { lineMat.opacity = 0; if (dotRef.current) dotRef.current.material.opacity = 0; return; }
    s.t = Math.min(2, s.t + d / 0.8);
    const travel = Math.min(1, s.t);
    const head = new THREE.Vector3().lerpVectors(s.start, s.end, travel);
    geom.setFromPoints([s.start, head]);
    const op = s.t < 1 ? 0.95 : Math.max(0, 0.95 * (2 - s.t));
    lineMat.opacity = op;
    if (dotRef.current) { dotRef.current.position.copy(head); dotRef.current.material.opacity = op; }
    if (s.t >= 2) s.active = false;
  });

  return (
    <group>
      <line geometry={geom}><primitive object={lineMat} attach="material" /></line>
      <mesh ref={dotRef}>
        <sphereGeometry args={[2.2, 12, 12]} />
        <meshBasicMaterial color="#7fefff" transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </group>
  );
}

function GalaxyScene({ data, selectedId, focusToken, onSelect, onDeepDive, onReturnSolar, showConstellations, controlsRef }) {
  const [hovered, setHovered] = useState(null);
  const sprites = useSprites();
  const posMap = useMemo(() => {
    const m = {};
    [...data.heroes, ...data.stars].forEach((o) => { if (o.galaxy_pos) m[o.id] = o.galaxy_pos; });
    m["sun"] = data.sun_pos;
    return m;
  }, [data]);
  return (
    <>
      <ambientLight intensity={0.7} />
      <GalaxyBody sprites={sprites} />
      {showConstellations && <Constellations />}
      {data.stars.map((s) => (
        <Marker key={s.id} obj={s} hero={false} selected={selectedId === s.id} onSelect={onSelect} onDeepDive={onDeepDive} hovered={hovered} setHovered={setHovered} sprite={sprites.glow} />
      ))}
      {data.heroes.map((h) => (
        <Marker key={h.id} obj={h} hero selected={selectedId === h.id} onSelect={onSelect} onDeepDive={onDeepDive} hovered={hovered} setHovered={setHovered} sprite={sprites.glow} />
      ))}
      <SunMarker pos={data.sun_pos} onReturn={onReturnSolar} sprite={sprites.glow} />
      <GalaxyCamera focusId={selectedId} focusToken={focusToken} posMap={posMap} controlsRef={controlsRef} />
      <FocusTrail focusId={selectedId} focusToken={focusToken} posMap={posMap} />
      <OrbitControls ref={controlsRef} enablePan enableDamping dampingFactor={0.08} minDistance={4} maxDistance={620} maxPolarAngle={Math.PI / 2.02} makeDefault />
      <EffectComposer>
        <Bloom intensity={1.05} luminanceThreshold={0.0} luminanceSmoothing={0.9} mipmapBlur radius={0.8} />
        <Vignette eskil={false} offset={0.22} darkness={0.9} />
      </EffectComposer>
    </>
  );
}

export default function GalaxyMap({ selectedId, focusToken, onSelect, onDeepDive, onReturnSolar, showConstellations }) {
  const controlsRef = useRef();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/galaxy/stars").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <Canvas camera={{ position: [0, 150, 285], fov: 55, near: 0.1, far: 5000 }}
      gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping }}
      style={{ background: "#020207" }} data-testid="galaxy-canvas">
      <color attach="background" args={["#020207"]} />
      <GalaxyScene data={data} selectedId={selectedId} focusToken={focusToken} onSelect={onSelect}
        onDeepDive={onDeepDive} onReturnSolar={onReturnSolar} showConstellations={showConstellations} controlsRef={controlsRef} />
    </Canvas>
  );
}
